import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Building2, Satellite, BellRing, Users, GaugeCircle, MapPin, Fuel, Car, Download, Loader2, Hash } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import AgencySettings from "./settings/AgencySettings";
import PolicySettings from "./settings/PolicySettings";
import GeotabSettings from "./settings/GeotabSettings";
import NotificationSettings from "./settings/NotificationSettings";
import UsersSettings from "./settings/UsersSettings";
import UserDetail from "./settings/UserDetail";
import LocationSettings from "./settings/LocationSettings";
import FuelPriceSettings from "./settings/FuelPriceSettings";
import CategorySettings from "./settings/CategorySettings";
import SiriSettings from "./settings/SiriSettings";

const SECTIONS = [
  { slug: "", label: "Profil Agensi", desc: "Nama, alamat & maklumat perhubungan agensi", icon: Building2 },
  { slug: "lokasi", label: "Cawangan & Lokasi", desc: "Urus pejabat, cawangan & lokasi bertolak di peta", icon: MapPin },
  { slug: "polisi", label: "Polisi & Kawalan Perjalanan", desc: "Bajet bahan api & had jarak tempahan", icon: GaugeCircle },
  { slug: "harga-minyak", label: "Harga Minyak", desc: "Urus harga seliter & Harga BUDI setiap jenis minyak", icon: Fuel },
  { slug: "kategori-kenderaan", label: "Kategori Kenderaan", desc: "Tambah, susun & urus kategori beserta imej", icon: Car },
  { slug: "no-siri", label: "No. Siri Laporan", desc: "Format & nombor turutan No. Siri laporan WP 4.1", icon: Hash },
  { slug: "telematik", label: "Telematik", desc: "Sambungan API, penjanaan key & ujian sambungan", icon: Satellite },
  { slug: "notifikasi", label: "Notifikasi & Emel", desc: "Kawalan pemberitahuan sistem", icon: BellRing },
  { slug: "pengguna", label: "Pengurusan Pengguna", desc: "Jemput & urus pengguna agensi", icon: Users },
];

export default function SettingsPage() {
  const { section = "", id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadBson = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/settings/backup-bson", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enfleet_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sandaran BSON berjaya dimuat turun");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const reload = () => api.get("/settings").then((r) => setTenant(r.data));
  useEffect(() => { reload(); }, []);

  const active = SECTIONS.find((s) => s.slug === section) || SECTIONS[0];

  const renderSection = () => {
    if (!tenant) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;
    const props = { tenant, reload };
    switch (active.slug) {
      case "lokasi": return <LocationSettings />;
      case "polisi": return <PolicySettings {...props} />;
      case "harga-minyak": return <FuelPriceSettings />;
      case "kategori-kenderaan": return <CategorySettings />;
      case "no-siri": return <SiriSettings />;
      case "telematik": return <GeotabSettings {...props} />;
      case "notifikasi": return <NotificationSettings {...props} />;
      case "pengguna": return id ? <UserDetail id={id} /> : <UsersSettings />;
      default: return <AgencySettings {...props} />;
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="settings-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tetapan</h1>
          <p className="text-sm text-muted-foreground mt-1">Konfigurasi agensi, integrasi dan pemberitahuan sistem.</p>
        </div>
        <button
          onClick={downloadBson}
          disabled={downloading}
          data-testid="download-bson-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#10056E] shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-60 shrink-0"
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloading ? "Memuat turun..." : "Download BSON"}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-72 shrink-0">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const isActive = s.slug === active.slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => navigate(s.slug ? `/tetapan/${s.slug}` : "/tetapan")}
                  data-testid={`settings-nav-${s.slug || "agensi"}`}
                  className={`flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors shrink-0 lg:shrink lg:w-full ${
                    isActive ? "bg-[#10056E] text-white" : "hover:bg-slate-100 text-slate-700"
                  }`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isActive ? "text-white" : "text-[#10056E]"}`} />
                  <span>
                    <span className="block text-sm font-semibold leading-tight">{s.label}</span>
                    <span className={`hidden lg:block text-[11px] mt-0.5 ${isActive ? "text-white/70" : "text-muted-foreground"}`}>{s.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="flex-1 min-w-0" data-testid={`settings-section-${active.slug || "agensi"}`}>
          {renderSection()}
        </div>
      </div>
    </div>
  );
}
